import { validateLoad } from "./performedRules";

// Phase 9C data layer for LAST / Suggested (blueprint §13). The server builds
// the history; this module only reads it for one current workout item. It
// renders nothing and never writes the execution document.

const EMPTY_ENTRY = Object.freeze({
  last: null,
  suggested: null,
  lastUsedUnit: null,
  lastUsedAt: null,
});

function plainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

// The local record keeps three maps under `server.history`:
// - byExerciseKey: the Start entries, one per prescribed slot (authoritative);
// - byExerciseId: the first occurrence of each exercise (compatibility);
// - byItemId: local only, the GET result of an added or substituted item.
// An absent or older history reads as empty maps.
export function normalizeHistory(history) {
  const source = plainObject(history);
  return {
    byExerciseKey: plainObject(source.byExerciseKey),
    byExerciseId: plainObject(source.byExerciseId),
    byItemId: plainObject(source.byItemId),
  };
}

export function mergeHistory(base, incoming) {
  const left = normalizeHistory(base);
  const right = normalizeHistory(incoming);
  return {
    byExerciseKey: { ...left.byExerciseKey, ...right.byExerciseKey },
    byExerciseId: { ...left.byExerciseId, ...right.byExerciseId },
    byItemId: { ...left.byItemId, ...right.byItemId },
  };
}

export function withItemHistory(history, itemId, entry) {
  const current = normalizeHistory(history);
  return { ...current, byItemId: { ...current.byItemId, [itemId]: entry } };
}

function origin(item) {
  return String(item?.origin || "").toUpperCase();
}

// History for one current workout item. A prescribed item is addressed by its
// slot key, so the same exercise at two ordinals keeps two entries. A slot's
// Start entry belongs to the exercise it prescribed: a substituted or added
// item only uses its own fetched entry. The exercise-level entry is a
// compatibility fallback; for an item that is not the prescribed one it keeps
// the facts (LAST, unit memory) but never the Suggested, which was computed
// for another slot and prescription.
export function historyEntryForItem(record, item) {
  if (!item) return EMPTY_ENTRY;
  const history = normalizeHistory(record?.server?.history);
  const own = history.byItemId[item.itemId];
  if (own) return { ...EMPTY_ENTRY, ...own };
  const exerciseLevel = history.byExerciseId[item.exerciseId];
  if (origin(item) === "PRESCRIBED") {
    const slot = item.prescribedExerciseKey
      ? history.byExerciseKey[item.prescribedExerciseKey]
      : null;
    return { ...EMPTY_ENTRY, ...(slot || exerciseLevel || {}) };
  }
  return exerciseLevel
    ? { ...EMPTY_ENTRY, ...exerciseLevel, suggested: null }
    : EMPTY_ENTRY;
}

// GET /api/workout-log/history for one added or substituted item (A.3). The
// execution id lets the server use its own context; a substituted item names
// its prescribed slot so today's prescription is found.
export function historyQueryForItem(record, item) {
  if (!record?.executionId || !item?.exerciseId) return null;
  const substituted = origin(item) === "SUBSTITUTED" && Boolean(item.prescribedExerciseKey);
  return {
    exerciseIds: item.exerciseId,
    contextKey: record.server?.contextKey || `execution:${record.executionId}`,
    ordinal: Math.max(1, Number(item.position) || 1),
    executionId: record.executionId,
    ...(substituted ? { prescribedExerciseKey: item.prescribedExerciseKey } : {}),
  };
}

export function historyEntryFromResponse(response, item) {
  const history = normalizeHistory(response);
  return history.byExerciseKey[item?.prescribedExerciseKey] ||
    history.byExerciseId[item?.exerciseId] ||
    null;
}

// Only an item the Start history cannot describe needs its own fetch.
export function needsOwnHistory(item) {
  return ["ADDED", "SUBSTITUTED"].includes(origin(item)) && Boolean(item?.exerciseId);
}

function isWarmup(set) {
  return String(set?.setType || "").toUpperCase() === "WARMUP";
}

function bySetIndex(left, right) {
  return Number(left.setIndex) - Number(right.setIndex);
}

// Mirror of the backend mapLastToSets (§13.4): by role, then by order. The
// server already sends performed reference sets only. A set without a
// counterpart has no LAST; nothing is fabricated.
export function lastBySetId(item, entry) {
  const result = {};
  const reference = Array.isArray(entry?.last?.sets) ? entry.last.sets : [];
  [true, false].forEach((warmup) => {
    const today = (item?.sets || []).filter((set) => isWarmup(set) === warmup).sort(bySetIndex);
    const last = reference.filter((set) => isWarmup(set) === warmup).sort(bySetIndex);
    today.forEach((set, index) => {
      if (last[index]) result[set.setId] = last[index];
    });
  });
  return result;
}

// The first working set is the only one that may carry Suggested (§13.5).
export function firstWorkingSet(item) {
  return (item?.sets || []).filter((set) => !isWarmup(set)).sort(bySetIndex)[0] || null;
}

export function isAllowedLoadIncrement(value, loadMode, convention, unit) {
  if (value == null) return true;
  const amount = Number(value);
  const step = loadMode === "OLYMPIC_BARBELL" && convention === "PER_SIDE"
    ? unit === "KG" ? 1.25 : 2.5
    : 0.5;
  return Number.isInteger(amount / step);
}

// A LAST load is usable for the LAST-assisted entry only when it can be
// entered as-is in today's field: same unit, same convention, and valid for
// today's load mode and increment. No conversion (§13.10). `barWeight` is
// today's, since a per-side value is entered as-is whatever the bar was.
export function usableLastLoad(lastSet, { loadMode, convention, unit, barWeight = null } = {}) {
  const load = lastSet?.load;
  if (!load || load.value == null || !unit || !convention) return null;
  if (load.unit !== unit || load.convention !== convention) return null;
  const candidate = {
    value: String(Number(load.value)),
    unit,
    convention,
    barWeight: loadMode === "OLYMPIC_BARBELL" && convention === "PER_SIDE" ? barWeight : null,
  };
  if (!validateLoad(candidate, loadMode)) return null;
  if (!isAllowedLoadIncrement(candidate.value, loadMode, convention, unit)) return null;
  return candidate;
}

export function usableLastReps(lastSet) {
  const reps = lastSet?.reps;
  return Number.isInteger(reps) && reps >= 1 && reps <= 100 ? reps : null;
}

// §12.7 step 2: the remembered per-exercise unit applies when it is newer than
// the last global unit change.
export function rememberedUnit(entry, preferences = {}) {
  const unit = entry?.lastUsedUnit;
  if (!["LB", "KG"].includes(unit)) return null;
  const changedAt = preferences?.weightUnitUpdatedAt
    ? new Date(preferences.weightUnitUpdatedAt).getTime()
    : null;
  if (changedAt == null || !Number.isFinite(changedAt)) return unit;
  const usedAt = entry.lastUsedAt ? new Date(entry.lastUsedAt).getTime() : Number.NaN;
  return Number.isFinite(usedAt) && usedAt > changedAt ? unit : null;
}
